import { useEffect, useMemo, useState, useRef } from "react";
import { Search, Loader2, FolderKanban, Inbox as InboxIcon, Phone, Video, Archive, ArchiveRestore } from "lucide-react";
import toast from "react-hot-toast";
import { useChatStore } from "../store/useChatStore.js";
import { useAuthStore } from "../store/useAuthStore";
import ChatContainer from "../components/ChatContainer.js";
import MessageInput from "../components/MessageInput";
import InboxProjectPanel from "../components/chat/InboxProjectPanel";
import InboxAIAssistant from "../components/chat/InboxAIAssistant";
import { axiosInstance } from "../lib/axios";

const STATUS_STYLE: Record<string, string> = {
  "In Progress": "bg-primary-container text-on-primary-container",
  "Not Started": "bg-surface-container-highest text-on-surface-variant",
  "On Hold": "bg-amber-100 text-amber-700",
  Completed: "bg-secondary-container text-on-secondary-container",
  Cancelled: "bg-error-container text-error",
};
const initials = (n = "") => n.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const Chat = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const { selectedUser, setSelectedUser, setActiveProjectId, messages, users, getUsers } = useChatStore();
  const { authUser } = useAuthStore();
  // The global Navbar (fixed, h-16) only renders for admins/managers. BDs have
  // no fixed navbar, and the client portal embeds this page below an in-flow
  // top bar — so only admins need to push the content down past the navbar.
  const needsTopOffset = !embedded && authUser?.role !== "Business Developer";
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  // Project-scoped conversation search (messages, file names, links, senders).
  const [searchOpen, setSearchOpen] = useState(false);
  const [msgQ, setMsgQ] = useState("");
  // Admin monitoring filters — narrow conversations by BD / Upwork account.
  const isAdminMonitor = !!authUser && !["Business Developer", "Client"].includes(authUser.role);
  const [bdFilter, setBdFilter] = useState("");
  const [acctFilter, setAcctFilter] = useState("");

  const loadProjects = async () => {
    try {
      const { data } = await axiosInstance.get("/project/inbox-projects");
      setProjects(data.projects || []);
    } catch { setProjects([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProjects(); getUsers(); }, []);

  // Live-refresh the inbox project panel + milestone data when ERP pushes an
  // update for a project (instant via socket).
  useEffect(() => {
    const onSync = () => loadProjects();
    window.addEventListener("cre:project-sync", onSync);
    return () => window.removeEventListener("cre:project-sync", onSync);
  }, []);

  // Reliable fallback: refetch every 12s while the tab is visible so ERP → CRE
  // updates surface without a manual reload even if the live socket doesn't fire.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") loadProjects();
    }, 12000);
    return () => clearInterval(id);
  }, []);

  const active = useMemo(() => projects.find((p) => p._id === activeId) || null, [projects, activeId]);

  // Unread + last-activity per project — cross-referenced from the chat store's
  // contact list (keyed by the conversation peer).
  const peerUser = (p: any) => {
    const peerId = p.peer?.userId || p.client?.userId;
    return peerId ? users.find((u) => u._id === peerId) : undefined;
  };
  const unreadFor = (p: any) => peerUser(p)?.unreadCount || 0;
  const lastActivity = (p: any) => {
    const t = peerUser(p)?.lastMessageAt;
    return t ? new Date(t).getTime() : new Date(p.createdAt || 0).getTime();
  };
  // Sort so the project with the most recent conversation sits on top.
  const byRecency = (list: any[]) => [...list].sort((a, b) => lastActivity(b) - lastActivity(a));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeGroup = useMemo(() => byRecency(projects.filter((p) => !p.archived)), [projects, users]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const archivedGroup = useMemo(() => byRecency(projects.filter((p) => p.archived)), [projects, users]);

  // Archive / unarchive the active project's conversation.
  const archiveProject = async (p: any, archived: boolean) => {
    try {
      await axiosInstance.patch(`/project/${p._id}/inbox-archive`, { archived });
      toast.success(archived ? "Conversation archived" : "Conversation restored");
      await loadProjects();
    } catch {
      toast.error("Couldn't update archive state");
    }
  };

  // Open a project: set the conversation to its peer + show its context.
  const openProject = (p: any) => {
    if (!p?._id || p._id === activeId) return; // idempotent — avoids update loops
    setActiveId(p._id);
    setActiveProjectId(p._id);
    const peer = p.peer || (p.client?.userId ? { userId: p.client.userId, name: p.client.name } : null);
    if (peer?.userId) {
      setSelectedUser({
        _id: peer.userId,
        fullName: peer.name,
        name: peer.name,
        email: "",
        profilePic: (peer as any).profilePic || "",
      } as any);
    } else {
      setSelectedUser(null as any);
    }
  };

  // Start a voice/video call — records it with the active project + conversation.
  const startCall = async (type: "voice" | "video") => {
    if (!active) return;
    try {
      await axiosInstance.post("/calls", { type, peer: active.peer?.userId || active.client?.userId || null, projectId: active._id, projectName: active.name });
      toast.success(`${type === "video" ? "Video" : "Voice"} call started for ${active.name}`);
    } catch {
      toast.error("Couldn't start the call");
    }
  };

  // Auto-select the first active project ONCE, after projects first load.
  // A ref guard prevents re-entry when the sorted list's reference changes
  // (unread/recency updates) — otherwise it would loop.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (activeGroup.length) {
      autoSelectedRef.current = true;
      openProject(activeGroup[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup]);

  // Distinct BD / Upwork-account options from the loaded projects.
  const bdOptions = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p) => { if (p.bd?._id) m.set(p.bd._id, p.bd.name || "Unknown BD"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [projects]);
  const acctOptions = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach((p) => { if (p.upworkAccount?._id) m.set(p.upworkAccount._id, p.upworkAccount.name || "Account"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [projects]);

  const matchFilters = (p: any) =>
    p.name.toLowerCase().includes(q.toLowerCase()) &&
    (!bdFilter || p.bd?._id === bdFilter) &&
    (!acctFilter || p.upworkAccount?._id === acctFilter);

  const filteredActive = activeGroup.filter(matchFilters);
  const filteredArchived = archivedGroup.filter(matchFilters);

  const ProjectRow = ({ p, archived }: { p: any; archived?: boolean }) => {
    const isActive = p._id === activeId;
    const unread = unreadFor(p);
    const subtitle = p.coordinator ? `${p.coordinator}` : (p.client?.name || p.projectType || "—");
    return (
      <button
        onClick={() => openProject(p)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${isActive ? "bg-primary-container/30" : "hover:bg-surface-container-low"}`}
      >
        <div className={`w-10 h-10 rounded-lg grid place-items-center text-[12px] font-bold shrink-0 ${isActive ? "bg-primary text-white" : archived ? "bg-surface-container-high text-on-surface-variant" : "bg-primary/10 text-primary"}`}>
          {initials(p.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-[13px] font-semibold truncate ${isActive ? "text-primary" : "text-on-surface"}`}>{p.name}</div>
          <div className="text-[11px] text-on-surface-variant truncate">{archived ? p.projectStatus : subtitle}</div>
        </div>
        {unread > 0 ? (
          <span className="text-[10px] font-bold text-on-primary bg-primary rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center shrink-0">{unread}</span>
        ) : isActive ? (
          <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
        ) : null}
      </button>
    );
  };

  return (
    <div className={`h-[calc(100vh-4rem)] overflow-hidden bg-background ${needsTopOffset ? "mt-16" : ""}`}>
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-4 h-full">
        <div className="relative h-full bg-surface-container-lowest rounded-2xl border border-outline-variant card-shadow overflow-hidden flex">
          {/* Left: project sidebar (Active / Archived) */}
          <aside className="w-72 shrink-0 border-r border-outline-variant bg-surface-container-lowest flex flex-col">
            <div className="px-4 py-3.5 border-b border-outline-variant">
              <h2 className="text-[16px] font-bold text-on-surface">Projects</h2>
            </div>
            <div className="p-3 border-b border-outline-variant space-y-2">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="w-full pl-9 pr-2 py-2 rounded-lg bg-background border border-outline-variant text-[13px] outline-none focus:border-primary" />
              </div>
              {isAdminMonitor && (bdOptions.length > 1 || acctOptions.length > 0) && (
                <div className="flex gap-2">
                  {bdOptions.length > 1 && (
                    <select value={bdFilter} onChange={(e) => setBdFilter(e.target.value)} className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-background border border-outline-variant text-[12px] outline-none focus:border-primary" title="Filter by BD">
                      <option value="">All BDs</option>
                      {bdOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  )}
                  {acctOptions.length > 0 && (
                    <select value={acctFilter} onChange={(e) => setAcctFilter(e.target.value)} className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-background border border-outline-variant text-[12px] outline-none focus:border-primary" title="Filter by Upwork account">
                      <option value="">All accounts</option>
                      {acctOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto cre-scroll p-3 space-y-4">
              {loading ? (
                <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-primary" size={20} /></div>
              ) : (
                <>
                  <section>
                    <p className="text-[10px] font-bold text-outline uppercase tracking-[0.12em] px-2 mb-1.5">Active Projects</p>
                    {filteredActive.length === 0 && <p className="text-[12px] text-outline px-2 py-1">No active projects.</p>}
                    <div className="space-y-1">{filteredActive.map((p) => <ProjectRow key={p._id} p={p} />)}</div>
                  </section>
                  {filteredArchived.length > 0 && (
                    <section>
                      <p className="text-[10px] font-bold text-outline uppercase tracking-[0.12em] px-2 mb-1.5 flex items-center gap-1"><Archive size={11} /> Archived Projects</p>
                      <div className="space-y-1">{filteredArchived.map((p) => <ProjectRow key={p._id} p={p} archived />)}</div>
                    </section>
                  )}
                </>
              )}
            </div>
          </aside>

          {/* Center: project header + conversation + AI assistant */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Project header */}
            <div className="px-5 py-3 border-b border-outline-variant flex items-center justify-between bg-surface-container-lowest">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-12 h-12 bg-primary-container rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-inner shrink-0">
                  {active ? initials(active.name) : <InboxIcon size={18} />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[18px] font-semibold text-on-surface truncate max-w-[280px]">{active ? active.name : "Select a project"}</h3>
                    {active && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 ${STATUS_STYLE[active.projectStatus] || "bg-surface-container-highest text-on-surface-variant"}`}>{active.projectStatus}</span>}
                  </div>
                  {active && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500 border border-white" />
                      <span className="text-[12px] text-on-surface-variant truncate max-w-[220px]">{active.coordinator || active.client?.name || "Project Team"} (Coordinator)</span>
                      <span className="text-outline-variant">•</span>
                      <span className="text-[12px] text-outline">{active.projectType}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Voice / Video / Search — inherit the active project context */}
              {active && (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startCall("voice")} title="Voice call" className="p-2.5 text-primary hover:bg-primary-container rounded-lg transition-colors border border-outline-variant/30"><Phone size={18} /></button>
                  <button onClick={() => startCall("video")} title="Video call" className="p-2.5 text-primary hover:bg-primary-container rounded-lg transition-colors border border-outline-variant/30"><Video size={18} /></button>
                  <button onClick={() => { setSearchOpen((o) => !o); setMsgQ(""); }} title="Search conversation" className={`p-2.5 rounded-lg transition-colors ${searchOpen ? "text-primary bg-primary-container/30" : "text-outline hover:bg-surface-container"}`}><Search size={18} /></button>
                  <button onClick={() => archiveProject(active, !active.archived)} title={active.archived ? "Restore conversation" : "Archive conversation"} className="p-2.5 text-outline hover:bg-surface-container rounded-lg transition-colors">
                    {active.archived ? <ArchiveRestore size={18} /> : <Archive size={18} />}
                  </button>
                </div>
              )}
            </div>

            {/* Project-scoped conversation search */}
            {active && searchOpen && (
              <div className="px-5 py-2.5 border-b border-outline-variant bg-surface-container-low">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-outline" />
                  <input
                    autoFocus
                    value={msgQ}
                    onChange={(e) => setMsgQ(e.target.value)}
                    placeholder={`Search in ${active.name} — messages, files, links…`}
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant text-[13px] outline-none focus:border-primary"
                  />
                </div>
                {msgQ.trim() && (() => {
                  const needle = msgQ.trim().toLowerCase();
                  const hits = (messages || []).filter((m: any) =>
                    (m.text || "").toLowerCase().includes(needle) ||
                    (m.file?.fileName || "").toLowerCase().includes(needle)
                  ).slice(-30).reverse();
                  return (
                    <div className="mt-2 max-h-56 overflow-y-auto cre-scroll space-y-1">
                      <p className="text-[11px] font-bold text-outline uppercase px-1">{hits.length} result{hits.length === 1 ? "" : "s"} in this project</p>
                      {hits.map((m: any) => (
                        <button
                          key={m._id}
                          onClick={() => document.getElementById(`msg-${m._id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                          className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-surface-container transition-colors"
                        >
                          <span className="text-[12px] text-on-surface line-clamp-1">
                            {m.messageType === "system" ? "🔔 " : ""}{m.file?.fileName ? `📎 ${m.file.fileName} — ` : ""}{m.text || ""}
                          </span>
                          <span className="text-[10px] text-outline block">{m.createdAt ? new Date(m.createdAt).toLocaleString() : ""}</span>
                        </button>
                      ))}
                      {hits.length === 0 && <p className="text-[12px] text-outline px-1 py-1">Nothing in this project's conversation matches.</p>}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Conversation */}
            <div className="flex-1 flex flex-col min-h-0 bg-white">
              {!active ? (
                <div className="flex-1 grid place-items-center text-center text-on-surface-variant p-8">
                  <div><FolderKanban size={40} className="mx-auto mb-3 text-outline" /><p className="text-sm font-semibold">Pick a project to open its Inbox.</p></div>
                </div>
              ) : !selectedUser ? (
                <div className="flex-1 grid place-items-center text-center text-on-surface-variant p-8">
                  <div><InboxIcon size={40} className="mx-auto mb-3 text-outline" /><p className="text-sm font-semibold">No portal account for this contact yet.</p><p className="text-[12px]">Provision the client to start a conversation. The project context is shown on the right.</p></div>
                </div>
              ) : (
                <>
                  <div className="flex-1 min-h-0 flex flex-col">
                    <ChatContainer showFileHistory={false} hideHeader hideComposer />
                  </div>
                  {/* Input area: AI overlay + composer */}
                  <div className="px-5 py-4 border-t border-outline-variant bg-surface-container-lowest">
                    <InboxAIAssistant projectName={active.name} />
                    <MessageInput />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: project + client context panel */}
          <InboxProjectPanel project={active} onProjectUpdated={loadProjects} />
        </div>
      </div>
    </div>
  );
};

export default Chat;
